import type { DesignElement } from '@canvas/shared-types';

export interface BrandColor {
  id: string;
  name: string;
  hex: string;
}

export interface BrandFont {
  id: string;
  name: string;
  family: string;
  sample: string;
  fontSize: number;
  fontWeight: number | string;
}

export interface BrandKit {
  id: string;
  name: string;
  colors: BrandColor[];
  fonts: BrandFont[];
  logo: DesignElement;
}

const BRAND_ICON_URL = '/brand/icon.png';

/** Default student/pro brand kit for Craftara demos (local, editable later via CMS). */
export const DEFAULT_BRAND_KIT: BrandKit = {
  id: 'craftara-default',
  name: 'Craftara Brand',
  colors: [
    { id: 'ink', name: 'Ink', hex: '#152033' },
    { id: 'teal', name: 'Teal', hex: '#0d7377' },
    { id: 'slate', name: 'Slate', hex: '#45566e' },
    { id: 'gold', name: 'Gold', hex: '#e5c07b' },
    { id: 'sky', name: 'Sky', hex: '#1b4d89' },
    { id: 'mist', name: 'Mist', hex: '#eef2f6' },
  ],
  fonts: [
    {
      id: 'display',
      name: 'Display',
      family: 'Outfit, Arial, sans-serif',
      sample: 'Headline',
      fontSize: 40,
      fontWeight: 700,
    },
    {
      id: 'body',
      name: 'Body',
      family: 'Source Sans 3, Arial, sans-serif',
      sample: 'Body copy',
      fontSize: 18,
      fontWeight: 400,
    },
    {
      id: 'label',
      name: 'Label',
      family: 'Outfit, Arial, sans-serif',
      sample: 'LABEL',
      fontSize: 14,
      fontWeight: 700,
    },
  ],
  logo: {
    elementId: 'brand-logo',
    name: 'Brand logo',
    kind: 'illustration',
    category: 'brand',
    tags: ['logo', 'brand'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'image', value: BRAND_ICON_URL, label: 'Logo' },
    defaultWidth: 120,
    defaultHeight: 120,
    factory: { type: 'image-url', url: BRAND_ICON_URL },
  },
};

export function brandFontAsElement(font: BrandFont): DesignElement {
  return {
    elementId: `brand-font-${font.id}`,
    name: font.name,
    kind: 'text',
    category: 'brand',
    tags: ['brand', 'font'],
    audience: 'all',
    license: 'free',
    preview: { mode: 'glyph', value: 'Aa', label: font.name },
    defaultWidth: 320,
    defaultHeight: 48,
    factory: {
      type: 'text',
      text: font.sample,
      fontSize: font.fontSize,
      fontWeight: font.fontWeight,
      fontFamily: font.family,
      fill: '#152033',
    },
  };
}
